import { toast } from 'react-toastify';

export interface ErrorType {
  response: { data: ErrorResponse };
}

export interface ErrorResponse {
  message?: string;
  details?: string;
}

/**
 * Backend messages lead with an error code such as "[12200-1]" so support can
 * trace them in the logs. The code means nothing to the person reading the
 * toast, so it is dropped before display. Code that branches on an error code
 * reads it from the raw response, which still has it.
 */
export function withoutErrorCode<T>(message: T): T {
  return (typeof message === 'string' ? message.replace(/\[\d+-\d+\]\s*/g, '').trim() : message) as T;
}

export function responseDataMessage(data: unknown): string {
  if (data == null) return '';
  if (typeof data === 'string') return data;
  if (typeof data === 'object' && 'message' in data && typeof (data as { message: unknown }).message === 'string') {
    return (data as { message: string }).message;
  }
  return '';
}

export function isJsonInvalidApiError(data: unknown): boolean {
  if (data == null || typeof data !== 'object') return false;
  const o = data as Record<string, unknown>;
  const code = String(o.code ?? o.error ?? o.type ?? '').toLowerCase();
  const msg = String(o.message ?? o.details ?? '').toLowerCase();
  if (code === 'json_invalid' || code.includes('json_invalid')) return true;
  if (msg.includes('json_invalid') || msg.includes('invalid json') || msg.includes('malformed json')) return true;
  return false;
}

export function displayCollectorUserPatchError(error: unknown): void {
  const toastConfig = { className: 'fs-unmask' };
  const ax = error as {
    response?: { status?: number; data?: unknown };
    code?: string;
    message?: string;
  };
  const status = ax.response?.status;
  const data = ax.response?.data;

  if (data && isJsonInvalidApiError(data)) {
    toast.error('Invalid request payload.', toastConfig);
    return;
  }

  if (status === 403) {
    toast.error(withoutErrorCode(responseDataMessage(data)) || 'You do not have access to this resource.', toastConfig);
    return;
  }
  if (status === 404) {
    toast.error(withoutErrorCode(responseDataMessage(data)) || 'User/record not found or not accessible.', toastConfig);
    return;
  }

  displayErrorDetails(error);
}

/** 403/404/422-style responses — do not treat 403 as session-expired (handled elsewhere if at all). */
export function tryDisplayHttpResourceError(error: unknown): boolean {
  const toastConfig = { className: 'fs-unmask' };
  const ax = error as { response?: { status?: number; data?: unknown } };
  const status = ax.response?.status;
  const data = ax.response?.data;

  if (status === 403) {
    toast.error(withoutErrorCode(responseDataMessage(data)) || 'You do not have access to this resource.', toastConfig);
    return true;
  }
  if (status === 404) {
    toast.error(withoutErrorCode(responseDataMessage(data)) || 'User/record not found or not accessible.', toastConfig);
    return true;
  }
  if (status === 422 || (status === 400 && isJsonInvalidApiError(data))) {
    toast.error(withoutErrorCode(responseDataMessage(data)) || 'Invalid request payload.', toastConfig);
    return true;
  }
  if (data && isJsonInvalidApiError(data)) {
    toast.error('Invalid request payload.', toastConfig);
    return true;
  }
  return false;
}

/**
 * Detects errors where a patient's email is already linked to a different client tag.
 * Returns true and shows the appropriate toast if this is a duplicate-client error.
 */
export function displayDuplicateClientError(error: unknown): boolean {
  const toastConfig = { className: 'fs-unmask' };
  const ax = error as { response?: { status?: number; data?: unknown } };
  const data = ax.response?.data as Record<string, unknown> | null | undefined;
  const msg = typeof data?.message === 'string' ? data.message : '';
  const details = typeof data?.details === 'string' ? data.details : '';
  const combined = `${msg} ${details}`.toLowerCase();

  const isDuplicate = combined.includes('client') && (combined.includes('already') || combined.includes('linked'));

  if (!isDuplicate) return false;

  // Attempt to extract the existing client name from the error message
  const nameMatch = msg.match(/(?:client|account)[^:]*[:\s]+['"]?([^'".,]+)['"]?/i);
  const clientName = nameMatch?.[1]?.trim() || 'another client';

  toast.error(
    `This email is already linked to an account with ${clientName}. Please contact support if you need help.`,
    toastConfig,
  );
  return true;
}

export type RescheduleCasePaymentErrorCode =
  | 'payment_profile_required'
  | 'invalid_payment_profile'
  | 'payment_authorization_failed';

/**
 * `PATCH patients/reschedule_case/:case_id` has no machine-readable error
 * code field on its error responses — these bracketed prefixes in the
 * message are the only stable, unique substrings distinguishing "this linked
 * case needs a payment profile before it can be scheduled" from "the profile
 * provided doesn't resolve" from "the charge itself failed". A genuine
 * reschedule of an already-paid appointment never returns any of these.
 */
export function getRescheduleCasePaymentErrorCode(error: unknown): RescheduleCasePaymentErrorCode | null {
  const ax = error as { response?: { data?: unknown } };
  const msg = responseDataMessage(ax.response?.data);

  if (msg.includes('[12210-11]')) return 'payment_profile_required';
  if (msg.includes('[12210-12]')) return 'invalid_payment_profile';
  if (msg.includes('[12210-13]')) return 'payment_authorization_failed';
  return null;
}

export function displayErrorDetails(error: any, networkErrorMessage?: string): void {
  if (
    networkErrorMessage &&
    (error?.code === 'ERR_NETWORK' ||
      error?.message === 'Network Error' ||
      error === 'ERR_NETWORK' ||
      error === 'Network Error')
  ) {
    toast.error(networkErrorMessage, { className: 'fs-unmask' });
    return;
  }

  const toastConfig = { className: 'fs-unmask' };

  if (tryDisplayHttpResourceError(error)) {
    return;
  }

  if (
    (error as ErrorType).response &&
    (error as ErrorType).response.data &&
    error.code !== 'ERR_NETWORK' &&
    error.message !== 'Network Error'
  ) {
    const { response } = error as ErrorType;
    if (response.data.message && response.data.details) {
      toast.error(withoutErrorCode(`${response.data.message} \n${response.data.details}`), toastConfig);
    } else if (response.data.details) {
      toast.error(withoutErrorCode(response.data.details), toastConfig);
    } else {
      toast.error(withoutErrorCode(response.data.message), toastConfig);
    }
  } else if (typeof error === 'string' && error !== 'ERR_NETWORK' && error !== 'Network Error') {
    toast.error(withoutErrorCode(error), toastConfig);
  } else if (error.code !== 'ERR_NETWORK' && error.message !== 'Network Error') {
    toast.error(withoutErrorCode((error as Error).message), toastConfig);
  }
}
